import { GuestAccount } from "../types";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "" // Use relative path in production
    : "http://localhost:3001"; // Use local server in development

// Initialize authToken from localStorage
let authToken: string | null = localStorage.getItem("token");

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
});

export const api = {
  login: async (username: string) => {
    const response = await fetch(`${API_BASE_URL}/api/login`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ username }),
    });

    if (!response.ok) {
      throw new Error("Login failed");
    }

    const data = await response.json();
    return {
      token: data.token,
      user: data.user,
    };
  },

  getCurrentUser: async () => {
    if (!authToken) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: headers(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setAuthToken(null);
          return null;
        }
        throw new Error("Failed to get user data");
      }

      return response.json();
    } catch (error) {
      console.error("Error getting current user:", error); // Debug log
      setAuthToken(null);
      return null;
    }
  },

  submitRSVP: async (guests: GuestAccount["guests"]) => {
    const response = await fetch(`${API_BASE_URL}/api/rsvp`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ guests }),
    });

    if (!response.ok) {
      throw new Error("Failed to submit RSVP");
    }

    return response.json();
  },

  getAllGuests: async () => {
    const response = await fetch(`${API_BASE_URL}/api/admin/guests`, {
      headers: headers(),
    });

    if (!response.ok) {
      throw new Error("Failed to get guests");
    }

    return response.json();
  },

  getAdminUsers: async (): Promise<
    { id: number; username: string; guestCount: number }[]
  > => {
    const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      headers: headers(),
    });

    if (!response.ok) {
      throw new Error("Failed to get users");
    }

    return response.json();
  },

  createUser: async (payload: { username: string }) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg =
        typeof errBody.error === "string" ? errBody.error : "Failed to create user";
      throw new Error(msg);
    }

    return response.json() as Promise<{
      id: number;
      username: string;
      guestCount: number;
    }>;
  },

  deleteUser: async (userId: number) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
      method: "DELETE",
      headers: headers(),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg =
        typeof errBody.error === "string" ? errBody.error : "Failed to delete user";
      throw new Error(msg);
    }

    return response.json();
  },

  createGuest: async (payload: {
    username: string;
    name: string;
    guestType: "adult" | "child" | "infant";
    foodPreferences?: string;
    isAttending?: number | null;
  }) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/guests`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg =
        typeof errBody.error === "string" ? errBody.error : "Failed to create guest";
      throw new Error(msg);
    }

    return response.json();
  },

  deleteGuest: async (guestId: number) => {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/guests/${guestId}`,
      {
        method: "DELETE",
        headers: headers(),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete guest");
    }

    return response.json();
  },

  getGifts: async () => {
    const response = await fetch(`${API_BASE_URL}/api/gifts`, {
      headers: headers(),
    });

    if (!response.ok) {
      throw new Error("Failed to get gifts");
    }

    return response.json();
  },

  reserveGift: async (giftId: number, quantity: number) => {
    const response = await fetch(
      `${API_BASE_URL}/api/gifts/${giftId}/reserve`,
      {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ quantity }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to reserve gift");
    }

    return response.json();
  },

  deleteReservation: async (giftId: number) => {
    const response = await fetch(
      `${API_BASE_URL}/api/gifts/${giftId}/reserve`,
      {
        method: "DELETE",
        headers: headers(),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to delete reservation");
    }

    return response.json();
  },

  createGift: async (gift: {
    title: string;
    description: string;
    totalQuantity: number;
    isActive: boolean;
  }) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/gifts`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(gift),
    });

    if (!response.ok) {
      throw new Error("Failed to create gift");
    }

    return response.json();
  },

  updateGift: async (
    giftId: number,
    gift: {
      title: string;
      description: string;
      totalQuantity: number;
      isActive: boolean;
    },
  ) => {
    const apiGift = {
      ...gift,
      isActive: !!gift.isActive,
    };
    const response = await fetch(`${API_BASE_URL}/api/admin/gifts/${giftId}`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify(apiGift),
    });

    if (!response.ok) {
      throw new Error("Failed to update gift");
    }

    return response.json();
  },

  deleteGift: async (giftId: number) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/gifts/${giftId}`, {
      method: "DELETE",
      headers: headers(),
    });

    if (!response.ok) {
      throw new Error("Failed to delete gift");
    }

    return response.json();
  },
};
